import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { displayName, type FamilyMember } from '@sfp/shared/family-member';
import { useAuth } from '../../lib/auth.js';
import { useCollection, orderBy } from '../../lib/hooks.js';
import { formatTimestamp } from '../../lib/format.js';

type StatusFilter = 'all' | 'active' | 'archived';

export function FamilyMembersList() {
  const { role } = useAuth();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');

  const { docs, loading } = useCollection<FamilyMember>('familyMembers', [
    orderBy('updatedAt', 'desc'),
  ]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return docs.filter((m) => {
      if (statusFilter !== 'all' && (m.status ?? 'active') !== statusFilter) return false;
      if (q && !displayName(m).toLowerCase().includes(q)) return false;
      return true;
    });
  }, [docs, statusFilter, search]);

  if (role !== 'admin') {
    return (
      <div className="mx-auto w-full max-w-6xl space-y-6 px-6 py-6">
        <div className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm">
          <h1 className="text-lg font-semibold text-stone-900">Admins only.</h1>
          <p className="mt-1 text-sm text-stone-500">
            You need administrator access to view family members.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 px-6 py-6">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Family members</h1>
        <Link
          to="/admin/family/new"
          className="rounded bg-[#BF5700] px-4 py-2 text-sm font-medium text-white hover:opacity-90"
        >
          Add family member
        </Link>
      </header>

      <div className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-1.5">
            <FilterChip
              active={statusFilter === 'all'}
              onClick={() => setStatusFilter('all')}
              label="All"
            />
            <FilterChip
              active={statusFilter === 'active'}
              onClick={() => setStatusFilter('active')}
              label="Active"
            />
            <FilterChip
              active={statusFilter === 'archived'}
              onClick={() => setStatusFilter('archived')}
              label="Archived"
            />
          </div>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name"
            className="rounded border border-stone-300 px-3 py-2 text-sm focus:border-stone-500 focus:outline-none"
          />
        </div>

        <div className="mt-5 overflow-hidden">
          {loading ? (
            <div className="px-2 py-10 text-center text-sm text-stone-400">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="px-2 py-10 text-center text-sm text-stone-500">
              No family members yet.{' '}
              <Link to="/admin/family/new" className="text-[#BF5700] hover:underline">
                Add your first family member
              </Link>
              .
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-stone-200 bg-stone-50 text-left text-xs uppercase tracking-wider text-stone-500">
                <tr>
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">Relationship</th>
                  <th className="px-4 py-2">Email</th>
                  <th className="px-4 py-2">Phone</th>
                  <th className="px-4 py-2">Tags</th>
                  <th className="px-4 py-2">Updated</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => {
                  const email = m.emails?.[0]?.email;
                  const phone = m.phones?.[0]?.number;
                  return (
                    <tr key={m.id} className="border-b border-stone-100 hover:bg-stone-50">
                      <td className="px-4 py-2">
                        <Link
                          to={`/admin/family/${m.id}`}
                          className="font-medium text-stone-900 hover:underline"
                        >
                          {displayName(m)}
                        </Link>
                      </td>
                      <td className="px-4 py-2 text-stone-700">
                        {m.relationshipToHead ? relLabel(m.relationshipToHead) : '—'}
                      </td>
                      <td className="px-4 py-2 text-stone-700">{email || '—'}</td>
                      <td className="px-4 py-2 text-stone-700">{phone || '—'}</td>
                      <td className="px-4 py-2">
                        {m.tags && m.tags.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {m.tags.map((t: string) => (
                              <span
                                key={t}
                                className="rounded-full bg-stone-100 px-2 py-0.5 text-xs text-stone-700"
                              >
                                {t}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-stone-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs text-stone-500">
                        {formatTimestamp(m.updatedAt as never)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
        active
          ? 'bg-stone-900 text-stone-50'
          : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
      }`}
    >
      {label}
    </button>
  );
}

function relLabel(slug: string): string {
  return slug
    .split('_')
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
}
